import { Button } from "@ui/Button";
import { Fieldset } from "@ui/Fieldset";
import { InputContainer } from "@ui/InputContainer";
import { InputError } from "@ui/InputError";
import { useLayoutAppKind } from "@ui/layouts/layoutProvider";
import { MaterialSymbol } from "@ui/MaterialSymbol";
import { Panel } from "@ui/Panel";
import { PanelFooter } from "@ui/PanelFooter";
import { Stack } from "@ui/Stack";
import { Text } from "@ui/typography/Text";
import { isValidationError422 } from "@utils/isValidationError.typeguard";
import { type Accessor, createEffect, createSignal, Show } from "solid-js";
import { createStore, type Store } from "solid-js/store";
import { deleteBoard, updateBoard } from "@/client";
import type { ListBoardItemResponse } from "@/client/types.gen.ts";
import { IconPicker } from "~/modules/agencyhub/groups/components/IconPicker";
import { useBoards } from "~/modules/shared/boards/boardsContext";
import { PrivacyRadioGroup } from "../../shared/PrivacyRadioGroup";

declare const pageVars: {
	boardId: string;
};

type Props = {
	board: Accessor<ListBoardItemResponse | undefined>;
	mutate: (data: ListBoardItemResponse) => void;
	isOwner: boolean;
};

type BoardUpdateDTO = {
	title: string;
	description: string;
	privacy: "public" | "private";
	clip_count: number;
	icon: string;
};
export const BoardSettingsButton = (props: Props) => {
	const [errors, setErrors] = createSignal<Record<string, string[]>>({});
	const [sideBarOpen, setSideBarOpen] = createSignal(false);

	const [formData, setFormData] = createStore<BoardUpdateDTO>({
		title: props.board()?.title ?? "",
		description: props.board()?.description ?? "",
		clip_count: props.board()?.clip_count ?? 0,
		privacy: (props.board()?.privacy as "public" | "private") ?? "private",
		icon: props.board()?.icon ?? "",
	});
	createEffect(() => {
		setFormData("title", props.board()?.title ?? "");
		setFormData("description", props.board()?.description ?? "");
		setFormData(
			"privacy",
			(props.board()?.privacy as "public" | "private") ?? "private",
		);
		setFormData("icon", props.board()?.icon ?? "");
	});

	return (
		<Show when={props.isOwner}>
			<Button
				variant="secondary"
				theme="monochrome"
				symbol="settings"
				onClick={() => {
					setSideBarOpen(!sideBarOpen());
				}}
			>
				Board details
			</Button>
			<Show when={props.board()}>
				<Panel
					isOpen={sideBarOpen}
					onToggle={() => {
						setSideBarOpen(!sideBarOpen());
					}}
					title="Board Settings"
					symbol="settings"
					class="board-settings-panel"
					footer={
						<PanelFooter>
							<Button
								type="button"
								variant="secondary"
								theme="monochrome"
								onClick={() => setSideBarOpen(false)}
							>
								Cancel
							</Button>
							<SaveButton
								formData={formData}
								setErrors={setErrors}
								mutate={props.mutate}
								board={props.board}
								setSideBarOpen={setSideBarOpen}
							/>
						</PanelFooter>
					}
				>
					<SettingsPanel
						board={props.board}
						formData={formData}
						setFormData={setFormData}
						setSideBarOpen={setSideBarOpen}
						errors={errors}
					/>
				</Panel>
			</Show>
		</Show>
	);
};

const SaveButton = (props: {
	formData: Store<BoardUpdateDTO>;
	setErrors: (errors: Record<string, string[]>) => void;
	mutate: (data: ListBoardItemResponse) => void;
	board: Accessor<ListBoardItemResponse | undefined>;
	setSideBarOpen: (value: boolean) => void;
}) => {
	const boards = useBoards();

	return (
		<Button
			type="submit"
			symbol="settings"
			variant="primary"
			onClick={async () => {
				props.setErrors({});
				const res = await updateBoard({
					body: {
						title: props.formData.title,
						description: props.formData.description,
						privacy: props.formData.privacy,
						icon: props.formData.icon || null,
					},
					path: {
						board_id: pageVars.boardId,
					},
				});
				if (isValidationError422(res)) {
					props.setErrors(res.error?.errors ?? {});
					return;
				}
				if (res.error) {
					props.setErrors(
						JSON.parse((res.error as any)?.message)?.errors ?? {},
					);
					return;
				}
				const previous = props.board();
				props.mutate({
					...(previous ?? {}),
					...res.data,
					clip_count: props.formData.clip_count,
				});
				boards.notifyBoardUpdated();
				props.setSideBarOpen(false);
			}}
		>
			Save changes
		</Button>
	);
};

const SettingsPanel = (props: {
	board: Accessor<ListBoardItemResponse | undefined>;
	formData: Store<BoardUpdateDTO>;
	setFormData: (data: BoardUpdateDTO) => void;
	setSideBarOpen: (value: boolean) => void;
	errors: Accessor<Record<string, string[]>>;
}) => {
	const appKind = useLayoutAppKind();
	const boards = useBoards();
	const showPrivacyIcon = appKind === "askgvt" || appKind === "studio";
	const handleDeleteBoard = async () => {
		await deleteBoard({
			path: {
				board_id: pageVars.boardId,
			},
		});
		boards.notifyBoardDeleted();
		props.setSideBarOpen(false);

		const boardsListHref =
			appKind === "labs"
				? `/projects/${props.board()?.project_id}/boards`
				: appKind === "studio"
					? "/studio/boards"
					: "/boards";

		window.location.href = `${boardsListHref}?deleted_board_id=${pageVars.boardId}`;
	};

	return (
		<form
			onSubmit={(e) => {
				e.preventDefault();
			}}
		>
			<InputContainer>
				<label for="title">Board name</label>
				<input
					type="text"
					id="title"
					value={props.formData?.title}
					onChange={(e) => {
						props.setFormData({
							...props.formData,
							title: e.target.value,
						});
					}}
				/>
				<InputError name="title" errors={props.errors} />
			</InputContainer>
			<InputContainer>
				<label for="description">
					<div>
						Description{" "}
						<Text color="fg3" size="small" as="span">
							(optional)
						</Text>
					</div>
				</label>
				<textarea
					id="description"
					placeholder="Enter board description"
					value={props.formData?.description}
					rows={4}
					onChange={(e) => {
						props.setFormData({
							...props.formData,
							description: e.target.value,
						});
					}}
				></textarea>
			</InputContainer>
			<IconPicker
				value={props.formData.icon}
				onChange={(icon) => {
					props.setFormData({
						...props.formData,
						icon,
					});
				}}
			/>

			<Show when={showPrivacyIcon}>
				<PrivacyRadioGroup
					value={props.formData.privacy}
					onChange={(value) => {
						props.setFormData({
							...props.formData,
							privacy: value,
						});
					}}
				/>
			</Show>
			<Fieldset>
				<legend>
					<MaterialSymbol symbol="delete" />
					<div>Board Deletion</div>
				</legend>
				<InputContainer>
					<Stack justify="start" direction="row">
						<Button
							onClick={() => {
								handleDeleteBoard();
							}}
							theme="monochrome"
							variant="secondary"
							symbol="delete"
						>
							Delete Board
						</Button>
					</Stack>

					<p class="explanation">
						This will delete the entire board and everything inside it.
					</p>
				</InputContainer>
			</Fieldset>
		</form>
	);
};
